import { overCap, type LevelConfig } from '@numslearn/shared';
import type { StringKey, Translate } from '../i18n/index.js';

/**
 * The line under the board that says what just happened.
 *
 * There are thirteen rules and two mechanics, and the honest sentence differs
 * for nearly all of them — which is the point of note 5: a multiples board
 * used to print «٣ = ٩», which is simply false. Every branch below produces a
 * statement that is true, and where it can, one that teaches: «٣ × ٣ = ٩» says
 * why the pair is a pair, not merely that it is.
 */

export interface Verdict {
  kind: 'match' | 'miss';
  labels: string[];
  values: number[];
  reason?: 'rule' | 'connection';
  /** The board was stranded after this match and its tiles were re-dealt. */
  reshuffled?: boolean;
}

interface Ctx {
  cfg: LevelConfig;
  t: Translate;
  /** Digits inside a tile label need converting too — «٢×٦», not «2×6». */
  num: (n: number | string) => string;
  /** Hunt boards only: how many targets are still out there. */
  targetsLeft: number;
}

export function verdictLine(v: Verdict | null, ctx: Ctx): string {
  const line = verdictBody(v, ctx);
  // Say so when the board changed under the child's fingers — otherwise the
  // new numbers look like a glitch.
  return v?.kind === 'match' && v.reshuffled ? `${line} · ${ctx.t('msg.reshuffled')}` : line;
}

function verdictBody(v: Verdict | null, { cfg, t, num, targetsLeft }: Ctx): string {
  if (!v) return '';
  const a = num(v.labels[0] ?? '');
  const b = num(v.labels[1] ?? '');
  const target = cfg.ruleTarget;

  if (v.kind === 'match') {
    switch (cfg.rule) {
      case 'sumTo':
      case 'sumTo20':
        return t('msg.match', { a, b, t: target });

      case 'multipleOf': {
        // The pair is (base, multiple) in whichever order it was tapped, so the
        // sentence is built from the values, not from the order of the taps.
        const lo = Math.min(v.values[0]!, v.values[1]!);
        const hi = Math.max(v.values[0]!, v.values[1]!);
        const k = lo === 0 ? 0 : hi / lo;
        return cfg.multipleDisplay === 'division'
          ? t('msg.matchDivide', { a: num(lo), b: num(hi), k: num(k) })
          : t('msg.matchFactor', { a: num(lo), b: num(hi), k: num(k) });
      }

      case 'productTo':
        return t('msg.matchProduct', { a, b, t: target });

      // Hunt rules: one tile, and what is left to find.
      case 'even': case 'odd': case 'prime':
      case 'notPrime': case 'multiplesOf': case 'factorsOf':
        return targetsLeft === 0 ? t('msg.foundAll') : t('msg.found', { a, n: targetsLeft });

      default:
        return t('msg.matchEq', { a, b });
    }
  }

  if (v.reason === 'connection') return t('msg.notConnected', { a, b });

  switch (cfg.rule) {
    case 'sumTo':
    case 'sumTo20':   return t('msg.noMatch', { a, b, t: target });
    case 'multipleOf': {
      // 3 ↔ 12 on a "up to × 3" board IS a multiple — saying it is not would
      // teach the wrong fact. Say what it is, and where this round stops.
      const lo = Math.min(v.values[0]!, v.values[1]!);
      const hi = Math.max(v.values[0]!, v.values[1]!);
      if (lo > 0 && hi % lo === 0 && overCap(lo, hi, cfg)) {
        return t('msg.overMultiplier', { a: num(lo), b: num(hi), k: num(hi / lo), m: num(cfg.maxMultiplier) });
      }
      return t('msg.noMatchMultiple', { a, b });
    }
    case 'productTo':  return t('msg.noMatchProduct', { a, b, t: target });
    case 'even':       return t('msg.notEven', { a });
    case 'odd':        return t('msg.notOdd', { a });
    case 'prime':      return t('msg.notPrimeMiss', { a });
    // The rule is "not prime", so the wrong tap IS a prime — saying "is not
    // prime" here would tell the child the opposite of what they got wrong.
    case 'notPrime':   return t('msg.isPrimeMiss', { a });
    case 'multiplesOf': return t('msg.notMultiple', { a, t: target });
    case 'factorsOf':   return t('msg.notFactor', { a, t: target });
    default:            return t('msg.noMatchEq', { a, b });
  }
}

/** The rule banner's string key: the capped wording when a multiplier cap is set. */
export const ruleKeyFor = (cfg: LevelConfig): StringKey =>
  (cfg.maxMultiplier > 0 && (cfg.rule === 'multipleOf' || cfg.rule === 'multiplesOf')
    ? `rule.${cfg.rule}UpTo`
    : `rule.${cfg.rule}`) as StringKey;

/** The rule banner at the top of the board, with its target and cap filled in. */
export const ruleLine = (cfg: LevelConfig, t: Translate): string =>
  t(ruleKeyFor(cfg), { n: cfg.ruleTarget, k: cfg.maxMultiplier });
